import type { ComponentPropsWithRef, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Two sizes, from the five the tree wrote it at: 9, 9.5, 10, 10.5 and 11 pixels
 * over twenty-four sites, with six trackings between them and the weight
 * flipping on no rule anyone could state. Two of those readings were a pixel
 * apart, which no reader sees and every author had to choose between.
 *
 * `sm` sits inside a card, close to what it names; `md` heads a section, and is
 * the default because a caption with nothing above it is the ordinary case.
 * `lg` heads a group of cards on a page, at the body's own size, in the ink:
 * Required and Optional at This Stage on Intake.
 */
const SIZE = {
  sm: "text-[9.5px] font-bold tracking-[0.06em]",
  md: "text-[11px] font-semibold tracking-[0.1em]",
  lg: "text-[15px] font-bold tracking-[0.05em] text-foreground",
} as const;

export type MicroLabelSize = keyof typeof SIZE;

/** A field's caption is a `label`; one heading a section is a heading. */
type LabelTag =
  "span" | "p" | "h2" | "h3" | "dt" | "th" | "label" | "legend" | "div";

interface Caption {
  size?: MicroLabelSize;
  children?: ReactNode;
}

export type MicroLabelProps<T extends LabelTag = "span"> = Caption & {
  as?: T;
} & Omit<ComponentPropsWithRef<T>, keyof Caption | "as">;

/**
 * The small uppercase caption that names what is under it.
 *
 * The colour is open and the rest is not: two sidebar captions read on a dark
 * ground and one names a figure in the brand's colour, which the muted grey
 * cannot say. Size, weight and tracking carry no such meaning.
 */
export function MicroLabel<T extends LabelTag = "span">({
  as,
  size = "md",
  className,
  children,
  ...rest
}: MicroLabelProps<T>) {
  const Element = (as ?? "span") as "span";
  const attributes = rest as ComponentPropsWithRef<"span">;
  return (
    <Element
      className={cn("uppercase text-subtle-foreground", SIZE[size], className)}
      {...attributes}
    >
      {children}
    </Element>
  );
}

/** The names themselves, so a test can assert the set rather than a sample of it. */
export const MICRO_LABEL_SIZES = Object.keys(SIZE) as readonly MicroLabelSize[];
